import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { LINE_ICONS, LineIconName } from '../../../@icons/line-icons';

/** Ton de la pastille : ce qu'elle annonce, jamais une couleur choisie à la main. */
export type TagTone = 'go' | 'harvest' | 'wind' | 'cold' | 'estimate' | 'stale';

/** Pictogrammes des pastilles : ceux de `@icons` (`LINE_ICONS`). */
export type TagIcon = LineIconName;

/** Le pictogramme qui va de soi pour chaque ton ; « estimé » n'en a pas. */
const TONE_ICONS: Record<TagTone, TagIcon | null> = {
  go: 'sprout',
  harvest: 'basket',
  wind: 'wind',
  cold: 'snowflake',
  estimate: null,
  stale: 'clock',
};

const TONE_CLASSES: Record<TagTone, string> = {
  go: 'bg-vw-surface-tint text-vw-primary-ink',
  harvest: 'bg-vw-warn-bg text-vw-warn-ink',
  wind: 'bg-vw-surface-sunk text-vw-body',
  cold: 'bg-vw-cold-bg text-vw-cold',
  // Le pointillé dit « calculé, pas constaté » (contrat de confiance) : pas de fond.
  estimate: 'border border-dashed border-vw-icon-idle text-vw-muted',
  stale: 'bg-vw-warn-bg text-vw-warn-ink',
};

/**
 * Pastille de statut (`vw-tag`) : « Feu vert », « À récolter », « Vent fort
 * demain », « estimé », « météo de dimanche 18:00 ». Un mot court, son
 * pictogramme, une couleur par ton. Elle informe et ne se touche pas : elle
 * vit dans une ligne ou un message, jamais seule comme bouton.
 *
 * Le contrat de confiance a deux tons à lui : `estimate` (pointillés, ce que
 * l'app calcule) et `stale` (ambre, ce qui n'est plus à jour, avec l'heure).
 * `vw-action-badge` reste la pastille « action requise » des cartes.
 *
 * `icon` remplace le pictogramme du ton ; `null` n'en met aucun.
 * Usage : `<vw-tag tone="go" [label]="'…' | translate" />`
 */
@Component({
  selector: 'vw-tag',
  standalone: true,
  imports: [NgComponentOutlet],
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`:host { display: inline-flex; max-width: 100%; vertical-align: middle; }`],
  template: `
    <span class="inline-flex min-w-0 items-center gap-1 rounded-pill px-1.5 text-caption font-semibold"
      [class]="toneClasses" [class.py-0.5]="tone !== 'estimate'" [class.py-px]="tone === 'estimate'" data-test="tag">
      @if (iconName; as name) {
        <span class="block h-[13px] w-[13px] shrink-0 [&_svg]:h-full [&_svg]:w-full" aria-hidden="true">
          <ng-container *ngComponentOutlet="icons[name]"></ng-container>
        </span>
      }
      <span class="truncate">{{ label }}</span>
    </span>
  `,
})
export class TagComponent {
  /** Texte déjà traduit, court : la pastille ne passe pas à la ligne. */
  @Input() label = '';
  @Input() tone: TagTone = 'go';
  /** Sans valeur, le pictogramme du ton ; `null`, aucun. */
  @Input() icon: TagIcon | null | undefined = undefined;

  readonly icons = LINE_ICONS;

  get toneClasses(): string {
    return TONE_CLASSES[this.tone] ?? TONE_CLASSES.go;
  }

  get iconName(): TagIcon | null {
    return this.icon === undefined ? TONE_ICONS[this.tone] : this.icon;
  }
}
